import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSidebar } from '@/contexts/SidebarContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Bell, 
  User, 
  LogOut, 
  Search, 
  MessageSquare, 
  CheckCheck, 
  ExternalLink,
  ChevronDown,
  Settings,
  HelpCircle,
  Sparkles,
  FileText,
  ShoppingCart,
  FileCheck,
  Building2,
  Users,
  Loader2,
  X,
  Menu
} from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/marketplace`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const { toggle: toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const notifRef = useRef(null);
  const userMenuRef = useRef(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchUnreadCount();
      const interval = setInterval(() => {
        fetchUnreadCount();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        setShowSearchResults(true);
      }
      if (e.key === 'Escape') {
        setShowSearchResults(false);
        searchInputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search function with debounce
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/search`, {
        params: { q: query, limit: 8 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowSearchResults(true);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
  };

  // Handle search result click
  const handleResultClick = (result) => {
    setShowSearchResults(false);
    setSearchQuery('');
    setSearchResults([]);
    navigate(result.link);
  };

  // Get icon for search result type
  const getResultIcon = (type) => {
    switch (type) {
      case 'rfq':
        return <FileText className="w-4 h-4 text-blue-600" />;
      case 'order':
        return <ShoppingCart className="w-4 h-4 text-emerald-600" />;
      case 'quotation':
        return <FileCheck className="w-4 h-4 text-purple-600" />;
      case 'vendor':
        return <Building2 className="w-4 h-4 text-amber-600" />;
      case 'customer':
        return <Users className="w-4 h-4 text-indigo-600" />;
      default:
        return <Search className="w-4 h-4 text-slate-400" />;
    }
  };

  // Get badge color for result type
  const getResultBadgeColor = (type) => {
    switch (type) {
      case 'rfq':
        return 'bg-blue-100 text-blue-700';
      case 'order':
        return 'bg-emerald-100 text-emerald-700';
      case 'quotation':
        return 'bg-purple-100 text-purple-700';
      case 'vendor':
        return 'bg-amber-100 text-amber-700';
      case 'customer':
        return 'bg-indigo-100 text-indigo-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.slice(0, 10));
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notif) => {
    if (!notif.is_read) {
      markAsRead(notif.id);
    }
    if (notif.link) {
      navigate(notif.link);
    }
    setShowNotifications(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/marketplace/login');
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_message':
        return <MessageSquare className="w-4 h-4 text-blue-600" />;
      default:
        return <Bell className="w-4 h-4 text-amber-500" />;
    }
  };

  const getUserInitials = () => {
    const name = user?.full_name || user?.name || 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getRoleLabel = () => {
    switch (user?.user_type) {
      case 'brand_manager': return 'Brand Manager';
      case 'vendor': return 'Vendor Partner';
      case 'admin': return 'Administrator';
      default: return 'User';
    }
  };

  if (!user) return null;

  return (
    <header className="bg-white border-b border-slate-200/80 sticky top-0 z-50 shadow-sm" data-testid="premium-navbar">
      <div className="h-16 px-4 lg:px-6 flex items-center justify-between">
        {/* Logo Section */}
        <div className="flex items-center gap-3 lg:gap-8">
          {/* Mobile hamburger */}
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2 text-slate-500 hover:text-blue-600 hover:bg-slate-100 rounded-xl transition-all"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div 
            className="flex items-center gap-3 lg:gap-3 cursor-pointer group" 
            onClick={() => navigate('/marketplace/dashboard')}
            data-testid="logo-link"
          >
            <img src="/logo-icon.png" alt="Brand Nexus" className="h-[40px] w-[40px] sm:h-[60px] sm:w-[60px] object-contain group-hover:scale-105 transition-transform" />
            <span className="hidden sm:inline font-extrabold text-[24px] text-slate-900 tracking-[2px]">BRAND NEXUS</span>
          </div>

          {/* Search Bar - Premium Style with Dropdown Results */}
          <div className="hidden md:block w-80 relative" ref={searchRef}>
            <div className="relative group">
              {isSearching ? (
                <Loader2 className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-600 w-4 h-4 animate-spin" />
              ) : (
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 group-focus-within:text-blue-600 transition-colors" />
              )}
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setShowSearchResults(true)}
                placeholder="Search RFPs, orders, vendors..."
                className="w-full pl-11 pr-12 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all"
                data-testid="search-input"
              />
              {searchQuery ? (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                    searchInputRef.current?.focus();
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : (
                <kbd className="hidden lg:flex absolute right-3 top-1/2 transform -translate-y-1/2 px-2 py-1 text-[10px] text-slate-400 bg-slate-100 rounded border border-slate-200">
                  ⌘K
                </kbd>
              )}
            </div>

            {/* Search Results Dropdown */}
            {showSearchResults && (searchQuery.length >= 2 || searchResults.length > 0) && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-200/80 z-50 overflow-hidden max-h-[400px] overflow-y-auto" data-testid="search-results-dropdown">
                {isSearching ? (
                  <div className="p-6 text-center">
                    <Loader2 className="w-6 h-6 text-blue-600 animate-spin mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Searching...</p>
                  </div>
                ) : searchResults.length > 0 ? (
                  <>
                    <div className="p-2 border-b border-slate-100 bg-slate-50/50">
                      <p className="text-xs text-slate-500 px-2">{searchResults.length} results for "{searchQuery}"</p>
                    </div>
                    <div className="py-1">
                      {searchResults.map((result, index) => (
                        <button
                          key={`${result.type}-${result.id}-${index}`}
                          onClick={() => handleResultClick(result)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                          data-testid={`search-result-${result.type}-${index}`}
                        >
                          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                            {getResultIcon(result.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-slate-900 truncate">{result.title}</p>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium uppercase ${getResultBadgeColor(result.type)}`}>
                                {result.type}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 truncate mt-0.5">{result.subtitle}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                ) : searchQuery.length >= 2 ? (
                  <div className="p-8 text-center">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Search className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-slate-600 font-medium">No results found</p>
                    <p className="text-xs text-slate-400 mt-1">Try different keywords</p>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Mobile Search Modal */}
          {showSearchResults && (
            <div className="md:hidden fixed inset-0 bg-white z-50" data-testid="mobile-search-modal">
              <div className="p-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setShowSearchResults(false);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    className="p-2 text-slate-500 hover:text-slate-700 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <div className="flex-1 relative">
                    {isSearching ? (
                      <Loader2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-600 w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    )}
                    <input
                      autoFocus
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      placeholder="Search RFPs, orders, vendors..."
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white"
                    />
                  </div>
                </div>
              </div>
              <div className="overflow-y-auto" style={{ height: 'calc(100vh - 80px)' }}>
                {isSearching ? (
                  <div className="p-8 text-center">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
                    <p className="text-slate-500">Searching...</p>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="py-2">
                    <p className="px-4 py-2 text-xs text-slate-500">{searchResults.length} results</p>
                    {searchResults.map((result, index) => (
                      <button
                        key={`mobile-${result.type}-${result.id}-${index}`}
                        onClick={() => handleResultClick(result)}
                        className="w-full flex items-center gap-3 px-4 py-4 hover:bg-slate-50 transition-colors text-left border-b border-slate-100"
                      >
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                          {getResultIcon(result.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium uppercase ${getResultBadgeColor(result.type)}`}>
                              {result.type}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-slate-900 truncate">{result.title}</p>
                          <p className="text-xs text-slate-500 truncate mt-0.5">{result.subtitle}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : searchQuery.length >= 2 ? (
                  <div className="p-12 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-lg font-medium text-slate-600">No results found</p>
                    <p className="text-sm text-slate-400 mt-1">Try different keywords</p>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <p className="text-slate-500">Type to search...</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2">
          {/* Mobile Search Button */}
          <button
            onClick={() => {
              setShowSearchResults(true);
              setTimeout(() => searchInputRef.current?.focus(), 100);
            }}
            className="md:hidden p-2.5 text-slate-500 hover:text-blue-600 hover:bg-slate-100 rounded-xl transition-all"
            data-testid="mobile-search-btn"
          >
            <Search className="w-5 h-5" />
          </button>

          {/* AI Design Quick Access - Brand Manager Only */}
          {user?.user_type === 'brand_manager' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/marketplace/ai-design')}
              className="hidden sm:flex items-center gap-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl px-3"
              data-testid="ai-design-btn"
            >
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">AI Design</span>
            </Button>
          )}

          {/* Notifications */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => {
                setShowNotifications(!showNotifications);
                if (!showNotifications) fetchNotifications();
              }}
              className="relative p-2.5 text-slate-500 hover:text-blue-600 hover:bg-slate-100 rounded-xl transition-all"
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-pulse">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200/80 z-50 overflow-hidden max-sm:-right-16">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                  <div>
                    <h3 className="font-semibold text-slate-900">Notifications</h3>
                    <p className="text-xs text-slate-500">{unreadCount} unread</p>
                  </div>
                  {unreadCount > 0 && (
                    <button 
                      onClick={markAllAsRead}
                      className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 font-medium"
                    >
                      <CheckCheck className="w-3.5 h-3.5" />
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="max-h-[400px] overflow-y-auto scrollbar-premium">
                  {notifications.length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Bell className="w-6 h-6 text-slate-400" />
                      </div>
                      <p className="text-slate-600 font-medium">All caught up!</p>
                      <p className="text-xs text-slate-400 mt-1">No new notifications</p>
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <div 
                        key={notif.id} 
                        onClick={() => handleNotificationClick(notif)}
                        className={`p-4 border-b border-slate-100 hover:bg-slate-50 cursor-pointer flex gap-3 transition-colors ${
                          !notif.is_read ? 'bg-blue-50/50' : ''
                        }`}
                      >
                        <div className="shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                          {getNotificationIcon(notif.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm ${!notif.is_read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                              {notif.title}
                            </p>
                            {!notif.is_read && (
                              <span className="w-2 h-2 bg-blue-600 rounded-full shrink-0 mt-1.5"></span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{notif.message}</p>
                          <span className="text-[10px] text-slate-400 mt-2 block">{formatTime(notif.created_at)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-3 border-t border-slate-100 bg-slate-50/50">
                  <button 
                    onClick={() => {
                      navigate('/marketplace/notifications');
                      setShowNotifications(false);
                    }}
                    className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium py-1"
                  >
                    View All Notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="w-px h-8 bg-slate-200 mx-2"></div>

          {/* User Profile Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-50 transition-all group"
              data-testid="user-menu-btn"
            >
              {/* Avatar */}
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-md">
                <span className="text-white text-sm font-bold">{getUserInitials()}</span>
              </div>
              
              {/* User Info */}
              <div className="hidden sm:block text-left">
                <p className="text-sm font-semibold text-slate-900 leading-tight">
                  {user.full_name?.split(' ')[0] || user.name?.split(' ')[0] || 'User'}
                </p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">
                  {getRoleLabel()}
                </p>
              </div>
              
              <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-slate-200/80 z-50 overflow-hidden">
                {/* User Header */}
                <div className="p-4 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-slate-100/50">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg">
                      <span className="text-white text-lg font-bold">{getUserInitials()}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{user.full_name || user.name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                  </div>
                </div>
                
                {/* Menu Items */}
                <div className="p-2">
                  <button
                    onClick={() => {
                      navigate('/marketplace/my-profile');
                      setShowUserMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 rounded-xl transition-colors"
                    data-testid="my-profile-btn"
                  >
                    <User className="w-4 h-4 text-slate-400" />
                    My Profile
                  </button>
                  <button
                    onClick={() => {
                      navigate('/marketplace/settings');
                      setShowUserMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 rounded-xl transition-colors"
                  >
                    <Settings className="w-4 h-4 text-slate-400" />
                    Settings
                  </button>
                  <button
                    onClick={() => {
                      navigate('/marketplace/help');
                      setShowUserMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 rounded-xl transition-colors"
                  >
                    <HelpCircle className="w-4 h-4 text-slate-400" />
                    Help Center
                  </button>
                </div>
                
                {/* Logout */}
                <div className="p-2 border-t border-slate-100">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
